import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { B2bBillingPeriod } from '../../../models/Contract';
import { ContractsService } from '../../../services/contracts.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { B2bContractPlateOption } from '../b2b-contract-plate-select/b2b-contract-plate-select.component';
import { exportB2bTableToExcel } from '../../../utils/b2b-table-export.util';

@Component({
  selector: 'app-b2b-billing-periods',
  templateUrl: './b2b-billing-periods.component.html',
  styleUrls: ['../shared/b2b-operational-shared.css', './b2b-billing-periods.component.css']
})
export class B2bBillingPeriodsComponent implements OnInit {
  contractId: number | null = null;
  contractNumber = '';
  contractName = '';
  items: B2bBillingPeriod[] = [];
  isLoading = false;
  submitting = false;
  showModal = false;
  viewMode = false;
  editingId: number | null = null;
  selectedVehicleId: number | null = null;
  filterYear: number | null = null;
  filterMonth: number | null = null;
  form!: FormGroup;

  readonly months = [
    { value: 1, label: 'Jan' }, { value: 2, label: 'Fev' }, { value: 3, label: 'Mar' },
    { value: 4, label: 'Abr' }, { value: 5, label: 'Mai' }, { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' }, { value: 8, label: 'Ago' }, { value: 9, label: 'Set' },
    { value: 10, label: 'Out' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dez' }
  ];

  columns = [
    { titulo: 'Ano', label: 'year' },
    { titulo: 'Mês', label: 'month', formatter: (v: any) => this.monthLabel(v) },
    { titulo: 'Placa', label: 'plate' },
    { titulo: 'Valor', label: 'amount', formatter: (v: any) => this.formatMoney(v), sensitive: true },
    { titulo: 'Pago', label: 'isPaid', formatter: (v: any) => (v ? 'Sim' : 'Não') },
    { titulo: 'Pago em', label: 'paidAt', formatter: (v: any) => this.formatDate(v) },
    { titulo: 'Referência', label: 'reference' }
  ];

  actions: TableAction[] = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view' },
    { label: 'Editar', icon: 'fas fa-edit', action: 'edit', class: 'btn-edit' },
    { label: 'Excluir', icon: 'fas fa-trash', action: 'delete', class: 'btn-delete' }
  ];

  get modalTitle(): string {
    if (this.viewMode) return 'Visualizar pagamento';
    return this.editingId ? 'Editar pagamento' : 'Novo pagamento';
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    const now = new Date();
    this.filterYear = now.getFullYear();

    this.form = this.fb.group({
      year: [now.getFullYear(), [Validators.required, Validators.min(2000)]],
      month: [now.getMonth() + 1, [Validators.required, Validators.min(1), Validators.max(12)]],
      plate: [''],
      amount: [null],
      isPaid: [false],
      paidAt: [''],
      reference: [''],
      notes: [''],
      b2bContractItemId: [null]
    });

    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.loadContractInfo();
        this.loadItems();
      }
    });
  }

  loadContractInfo(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (c) => {
        this.contractNumber = c.contractNumber || '';
        this.contractName = c.contractName || '';
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  loadItems(): void {
    if (!this.contractId) return;
    this.isLoading = true;
    this.contractsService.getB2bBillingPeriods(
      this.contractId,
      this.filterYear || undefined,
      this.filterMonth || undefined
    ).subscribe({
      next: (list) => {
        this.items = (list || []).map(x => this.normalize(x));
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  applyFilter(): void {
    this.loadItems();
  }

  openCreate(): void {
    const now = new Date();
    this.viewMode = false;
    this.editingId = null;
    this.selectedVehicleId = null;
    this.form.enable();
    this.form.reset({
      year: this.filterYear || now.getFullYear(),
      month: this.filterMonth || now.getMonth() + 1,
      plate: '',
      amount: null,
      isPaid: false,
      paidAt: '',
      reference: '',
      notes: '',
      b2bContractItemId: null
    });
    this.showModal = true;
  }

  openView(item: B2bBillingPeriod): void {
    this.patchForm(item);
    this.viewMode = true;
    this.form.disable();
    this.showModal = true;
  }

  openEdit(item: B2bBillingPeriod): void {
    this.patchForm(item);
    this.viewMode = false;
    this.form.enable();
    this.showModal = true;
  }

  private patchForm(item: B2bBillingPeriod): void {
    this.editingId = item.id ?? null;
    this.selectedVehicleId = item.vehicleId ?? null;
    this.form.patchValue({
      year: item.year,
      month: item.month,
      plate: item.plate || '',
      amount: item.amount ?? null,
      isPaid: !!item.isPaid,
      paidAt: this.toDateInput(item.paidAt),
      reference: item.reference || '',
      notes: item.notes || '',
      b2bContractItemId: item.b2bContractItemId ?? null
    });
  }

  onPlateSelected(option: B2bContractPlateOption | null): void {
    this.selectedVehicleId = option?.vehicleId ?? null;
  }

  closeModal(): void {
    this.showModal = false;
    this.viewMode = false;
    this.editingId = null;
    this.selectedVehicleId = null;
    this.form.enable();
  }

  submit(): void {
    if (this.viewMode || !this.contractId || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const payload: B2bBillingPeriod = {
      b2bContractId: this.contractId,
      year: Number(raw.year),
      month: Number(raw.month),
      vehicleId: this.selectedVehicleId,
      plate: raw.plate || null,
      amount: raw.amount != null && raw.amount !== '' ? Number(raw.amount) : null,
      isPaid: !!raw.isPaid,
      paidAt: raw.paidAt ? new Date(raw.paidAt).toISOString() : null,
      reference: raw.reference || null,
      notes: raw.notes || null,
      b2bContractItemId: raw.b2bContractItemId ? Number(raw.b2bContractItemId) : null
    };

    this.submitting = true;
    const req$ = this.editingId
      ? this.contractsService.updateB2bBillingPeriod(this.contractId, this.editingId, { ...payload, id: this.editingId })
      : this.contractsService.createB2bBillingPeriod(this.contractId, payload);

    req$.subscribe({
      next: () => {
        this.submitting = false;
        this.errorService.showSuccessMessage(this.editingId ? 'Pagamento atualizado.' : 'Pagamento criado.');
        this.closeModal();
        this.loadItems();
      },
      error: () => { this.submitting = false; }
    });
  }

  onActionClick(event: { action: string; item: B2bBillingPeriod }): void {
    if (event.action === 'view') this.openView(event.item);
    if (event.action === 'edit') this.openEdit(event.item);
    if (event.action === 'delete') this.deleteItem(event.item);
  }

  deleteItem(item: B2bBillingPeriod): void {
    if (!this.contractId || !item.id) return;
    const label = `${item.month}/${item.year}` + (item.plate ? ` (${item.plate})` : '');
    this.confirmationService.confirmDelete(label, 'o registro de pagamento').subscribe({
      next: (ok) => {
        if (!ok) return;
        this.contractsService.deleteB2bBillingPeriod(this.contractId!, item.id!).subscribe({
          next: () => {
            this.errorService.showSuccessMessage('Registro excluído.');
            this.loadItems();
          }
        });
      }
    });
  }


  exportExcel(): void {
    exportB2bTableToExcel(
      `b2b-pagamentos-${this.contractNumber || this.contractId}`,
      this.columns,
      this.items,
      'Pagamentos'
    );
  }

  navigateBack(): void {
    if (this.contractId) this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    else this.router.navigate(['/b2b/contracts']);
  }

  private normalize(dto: any): B2bBillingPeriod {
    return {
      id: dto.id ?? dto.Id,
      b2bContractId: dto.b2bContractId ?? dto.b2BContractId ?? dto.B2BContractId ?? this.contractId!,
      b2bContractItemId: dto.b2bContractItemId ?? dto.b2BContractItemId ?? dto.B2BContractItemId ?? null,
      vehicleId: dto.vehicleId ?? dto.VehicleId ?? null,
      plate: dto.plate ?? dto.Plate ?? null,
      year: dto.year ?? dto.Year,
      month: dto.month ?? dto.Month,
      amount: dto.amount ?? dto.Amount ?? null,
      isPaid: dto.isPaid ?? dto.IsPaid ?? false,
      paidAt: dto.paidAt ?? dto.PaidAt ?? null,
      reference: dto.reference ?? dto.Reference ?? null,
      notes: dto.notes ?? dto.Notes ?? null
    };
  }

  private toDateInput(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }

  formatDate(value: any): string {
    if (!value) return '—';
    try { return new Date(value).toLocaleDateString('pt-BR'); } catch { return String(value); }
  }

  formatMoney(value: any): string {
    if (value == null || value === '') return '—';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  monthLabel(value: number): string {
    return this.months.find(m => m.value === Number(value))?.label || String(value ?? '—');
  }
}
